import { createHttpClient } from '@adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReturnedApiAdapter } from '../../adapters/ReturnedApiAdapter';

// 🎯 Importar el tipo correcto del ConfirmModal
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';

interface CreateReturnedButtonProps {
  /** Si true, aplica estilos para estar dentro de EmptyView */
  inline?: boolean;
  /** Callback cuando la creación es exitosa */
  onSuccess?: () => void;
  /** Callback para refrescar los datos */
  onRefetch?: () => void;
}

/**
 * Componente para el botón de crear devolución
 * Maneja el modal de confirmación y permisos de creación
 */
export const CreateReturnedButton = ({
  inline = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  onSuccess,
  onRefetch,
}: CreateReturnedButtonProps) => {
  const { t } = useTranslation('returnedModule');
  const { toast } = useToast();
  const { canAccess } = usePermissions();
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  // Verificar si el usuario puede crear devoluciones
  const canCreate = canAccess(['tenant', 'tenant_3pl'], ['returns.view']);

  // 🎯 Función para confirmar la creación
  const handleConfirmCreate = useCallback(async () => {
    try {
      const httpClient = createHttpClient(apiClientWH);
      const returnedAdapter = new ReturnedApiAdapter(httpClient);
      const response = await returnedAdapter.createReturnedReception();

      if (response.success) {
        toast({
          title: t('toasts.success.title'),
          description: response.message || t('toasts.create.success'),
          variant: 'default',
        });

        // 🎯 Actualizar datos
        onSuccess?.();
        onRefetch?.();
      } else {
        toast({
          title: t('toasts.error.title'),
          description: response.message || t('toasts.create.error'),
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: t('toasts.error.title'),
        description: t('toasts.create.unexpected'),
        variant: 'destructive',
      });
    }
  }, [t, toast, onSuccess, onRefetch]);

  // 🎯 Función para manejar la creación de nueva recepción
  const handleCreateReturned = useCallback(() => {
    confirmModalRef.current?.open(
      handleConfirmCreate,
      undefined, // onCancel
      t('modal.create.title'),
      t('modal.create.message'),
      t('modal.create.confirm'),
      t('modal.create.cancel'),
    );
  }, [t, handleConfirmCreate]);

  // Si no tiene permisos, no mostrar el botón
  if (!canCreate) {
    return null;
  }

  return (
    <>
      <Button variant="primary" size="lg" onClick={handleCreateReturned} data-testid="create-returned-button">
        {t('buttons.create')}
      </Button>

      <ConfirmModal ref={confirmModalRef} />
    </>
  );
};
